import { create } from 'zustand';

type Theme = 'light' | 'dark' | 'system';

interface ThemeState {
  theme: Theme;
  resolved: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
  toggle: () => void;
}

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolveTheme(theme: Theme): 'light' | 'dark' {
  return theme === 'system' ? getSystemTheme() : theme;
}

function applyTheme(resolved: 'light' | 'dark') {
  document.documentElement.classList.toggle('dark', resolved === 'dark');
}

const stored = (typeof localStorage !== 'undefined' && localStorage.getItem('ivy-chat-theme')) as Theme | null;
const initial = stored || 'system';
const initialResolved = resolveTheme(initial);
applyTheme(initialResolved);

export const useIvyChatThemeStore = create<ThemeState>((set, get) => ({
  theme: initial,
  resolved: initialResolved,

  setTheme: (theme) => {
    const resolved = resolveTheme(theme);
    applyTheme(resolved);
    localStorage.setItem('ivy-chat-theme', theme);
    set({ theme, resolved });
  },

  toggle: () => {
    const current = get().resolved;
    const next = current === 'dark' ? 'light' : 'dark';
    applyTheme(next);
    localStorage.setItem('ivy-chat-theme', next);
    set({ theme: next, resolved: next });
  },
}));

if (typeof window !== 'undefined') {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    const state = useIvyChatThemeStore.getState();
    if (state.theme === 'system') {
      const resolved = getSystemTheme();
      applyTheme(resolved);
      useIvyChatThemeStore.setState({ resolved });
    }
  });
}
